import React, { memo } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { StaffTreeModule } from './types';
import { allCodes, toggleCode, toggleModule } from './staffUtils';
import { STAFF_PRIMARY } from './staffStyles';

interface StaffPermissionTreeProps {
  tree: StaffTreeModule[];
  /** Выбранные коды в виде normalizeSelection */
  value: string[];
  /** Не задан - дерево только для просмотра */
  onChange?: (codes: string[]) => void;
  disabled?: boolean;
}

const checkboxSx = {
  p: 0.75,
  color: '#cbd5e1',
  '&.Mui-checked': { color: STAFF_PRIMARY },
  '&.Mui-disabled.Mui-checked': { color: '#93c5fd' }
};

const labelSx = {
  m: 0,
  alignItems: 'flex-start',
  width: '100%',
  '& .MuiFormControlLabel-label': { pt: 0.75, minWidth: 0 },
  '& .MuiFormControlLabel-label.Mui-disabled': { color: 'inherit' }
};

/**
 * Галочки сотрудника: раздел = вкладка меню точки, внутри - права раздела. Права без вкладки не действуют,
 * поэтому при снятой вкладке они недоступны. Дерево строит сервер только из прав роли точки.
 */
const StaffPermissionTree: React.FC<StaffPermissionTreeProps> = ({ tree, value, onChange, disabled = false }) => {
  const readOnly = !onChange || disabled;

  if (tree.length === 0) {
    return (
      <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: '0.86rem' }}>
        У точки нет разделов, которые можно выдать сотруднику
      </Box>
    );
  }

  const has = (code: string): boolean => value.indexOf(code) >= 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {onChange && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
            Выдать можно только разделы и права, которые есть у точки
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button size="small" disabled={disabled} onClick={() => onChange(allCodes(tree))} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Выбрать все
            </Button>
            <Button size="small" disabled={disabled} onClick={() => onChange([])} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
              Снять все
            </Button>
          </Box>
        </Box>
      )}

      {tree.map((module) => {
        const menuOn = has(module.menu);
        const picked = module.items.filter(item => has(item.code)).length;
        return (
          <Box key={module.id} sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#ffffff', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, bgcolor: menuOn ? '#f8fafc' : '#ffffff' }}>
              <FormControlLabel
                sx={labelSx}
                disabled={readOnly}
                control={
                  <Checkbox
                    size="small"
                    sx={checkboxSx}
                    checked={menuOn}
                    onChange={(e) => onChange && onChange(toggleModule(value, module, e.target.checked))}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>
                    {module.name}
                  </Typography>
                }
              />
              {module.items.length > 0 && (
                <Typography sx={{ fontSize: '0.76rem', color: '#64748b', whiteSpace: 'nowrap', pr: 1 }}>
                  {menuOn ? `${picked} из ${module.items.length}` : 'Вкладка скрыта'}
                </Typography>
              )}
            </Box>

            {module.items.length > 0 && (
              <Box sx={{ borderTop: '1px solid #f1f5f9', py: 0.5, pl: { xs: 2.5, sm: 4 }, pr: 1.5 }}>
                {module.items.map(item => (
                  <FormControlLabel
                    key={item.code}
                    sx={labelSx}
                    disabled={readOnly || !menuOn}
                    control={
                      <Checkbox
                        size="small"
                        sx={checkboxSx}
                        checked={menuOn && has(item.code)}
                        onChange={(e) => onChange && onChange(toggleCode(value, item.code, e.target.checked))}
                      />
                    }
                    label={
                      <Box sx={{ opacity: menuOn ? 1 : 0.55 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#1e293b', lineHeight: 1.4 }}>{item.name}</Typography>
                        {item.description && (
                          <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.4 }}>{item.description}</Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(StaffPermissionTree);
